<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { to } from '~/routing'
import { useStore } from '~/store'
import {
  useObjectUrl,
  useShareLinks,
  useShareLinkMutations,
  type ShareableObject
} from '~/sharelink'

import CopyInput from '~/components/common/CopyInput.vue'
import DangerousButton from '~/components/common/DangerousButton.vue'
import Modal from '~/ui/Modal.vue'
import Button from '~/ui/Button.vue'
import Loader from '~/ui/Loader.vue'
import Gap from '~/ui/Gap.vue'
import Link from '~/ui/Link.vue'
import Heading from '~/ui/Heading.vue'
import ExpiryInput from '~/sharelink/ExpiryInput.vue'
import Alert from '~/ui/Alert.vue'

const props = defineProps<ShareableObject & { hasPrivateUploads?: () => boolean }>()

const isOpen = defineModel<boolean>()
const { t } = useI18n()
const store = useStore()

const { sharelinks, isLoading } = useShareLinks()
const existingLinks = computed(() => sharelinks.value
  .filter(link => link.object_id === props.object_id && link.object_type === props.object_type)
)
const objectUrl = useObjectUrl()
const { isSubmitting, updatingIds, revokingIds, create, updateExpiry, revoke } = useShareLinkMutations()

const submit = () => {
  create(props, null)
}
</script>

<template>
  <Modal
    v-model="isOpen"
    :title="t('sharelink.title')"
    cancel
  >
    <Loader v-if="isLoading" />
    <div
      class="layout-grid"
      style="
        grid-template-columns: 1fr max-content max-content;
        grid-auto-rows: minmax(64px, auto);
        align-items: end;
      "
    >
      <div
        ui-bleed
        ui-span-all
      >
        <CopyInput
          class="shape-field-full"
          :label="t('sharelink.publicLink')"
          :value="objectUrl(props)"
        />
      </div>
      <template v-if="(hasPrivateUploads ?? (() => true))()">
        <Gap ui-span-all style="border-block-start: 1px solid color-mix(in oklab, currentcolor 15%, transparent)" />
        <div
          class="layout-row"
          ui-span-all
        >
          <Heading
            :h2="t('sharelink.privateLinks')"
            section-heading
          />
          <Gap />
          <Link v-bind="to({ name: 'profile.sharelinks', params: { username: store.state.auth.username } })">
            {{ t('sharelink.manage') }}
          </Link>
        </div>

        <template
          v-for="link in existingLinks"
          :key="link.id"
        >
          <span class="layout-row">
            <CopyInput
              class="shape-field-grow"
              :value="objectUrl(props, link.token)"
              label=""
            />
          </span>
          <ExpiryInput
            :label="t(link.expires_at ? 'sharelink.expiresAt' : 'sharelink.expiresNever')"
            :model-value="link.expires_at ?? null"
            :disabled="updatingIds.has(link.id)"
            @update:model-value="updateExpiry(link, $event)"
          />
          <DangerousButton
            :is-loading="revokingIds.has(link.id)"
            class="color-destructive-ghost"
            ui-block-size="48"
            :title="t('sharelink.revokation.header')"
            @confirm="revoke(link)"
          >
            {{ t('sharelink.revoke') }}
            <template #content>
              {{ t('sharelink.revokation.warning') }}
            </template>
            <template #confirm>
              {{ t('sharelink.revokation.confirm') }}
            </template>
          </DangerousButton>
        </template>

        <form
          class="layout-grid"
          style="display: contents;"
          @submit.prevent="submit"
        >
          <Alert
            ui-span-all
            ui-bleed-inline
          >
            {{ t('sharelink.help') }}
            <template #actions>
              <Button
                :aria-busy="isSubmitting"
                class="color-primary-solid"
              >
                <Icon :icon="existingLinks.length > 0 ? 'bi:plus-lg' : 'bi:link-45deg'" />
                {{ existingLinks.length > 0 ? t('sharelink.generateAdditional') : t('sharelink.generate') }}
              </Button>
            </template>
          </Alert>
        </form>
      </template>
    </div>
  </Modal>
</template>
